import React, { useState } from "react";
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isObjectNotEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { Button, Modal } from "react-bootstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import ROUTES from "../constants/routes";
import Lead from "../models/Lead";
import { getProductNameBySku, getProgramById, getProgramBySlug } from "../utils/entityUtils";
import Encounter from "../models/Encounter";
import Subscription from "../models/Subscription";
import { FaArrowAltCircleRight, FaClipboardList, FaEnvelope, FaStethoscope } from "react-icons/fa";
import { FaWeightScale } from "react-icons/fa6";
import { fromUtcToDeviceTimeZoneAsMoment } from "../utils/dateFormatter";
import { getEncounterTreatmentName } from "../utils/productDisplayUtils";
import { useOrchestrator } from "../redux/ducks/orchestrator";
import { RiInfinityLine } from "react-icons/ri";
import { useAuth } from "../redux/ducks/auth";
import { useActionItems } from "../redux/ducks/actionItems";
import { useChat } from "../redux/ducks/chat";
import { useAnalytics } from "../redux/ducks/analytics";
import { useMediaQuery } from "react-responsive";
import { buildLeadFunnelPath } from "../utils/offerLinkUtils";

export const NotificationModalButton = ({ showByDefault = false }) => {
	const isSmallMobile = useMediaQuery({
		query: '(max-width: 400px)'
	});

	//#region useContexts

	const { showModal, setShowModal } = useActionItems();

	//#endregion
	//#region useEffects

	React.useEffect(() => {
		setShowModal(showByDefault);
	}, [showByDefault]);

	//#endregion

	return (
		<>
			<Button
				as="a"
				bsPrefix=" "
				className={`btn btn-icon navbar-icon rounded-circle ${isSmallMobile ? 'btn-sm' : ''}`}
				title="Action Items"
				onClick={() => { setShowModal(true); }}
			>
				<FaClipboardList size="18" />
			</Button>
			{showModal ? (
				<NotificationModal
					showModal={showModal}
					setShowModal={setShowModal}
				/>
			) : null}
		</>
	);
};

const NotificationModal = ({ showModal, setShowModal }) => {
	//#region useSearchParams

	const [searchParams, setSearchParams] = useSearchParams();

	//#endregion
	//#region useChat

	const { changeActiveEncounter } = useChat();

	//#endregion
	//#region useOrchestrator

	const { programs, products } = useOrchestrator();

	//#endregion
	//#region useAnalytics

	const { setLeadId } = useAnalytics();

	//#endregion
	//#region useNavigate

	const navigate = useNavigate();

	//#endregion
	//#region useLocation

	const location = useLocation();

	//#endregion
	//#region useContext

	const { profile } = useAuth();

	//#endregion
	//#region useStates

	const [leads, setLeads] = React.useState([]);
	const [encounters, setEncounters] = React.useState([]);
	const [subscriptions, setSubscriptions] = useState([]);

	//#endregion
	//#region button methods

	const goTo = (path, replace = true) => {
		navigate({ pathname: path, search: searchParams.toString() }, { state: { from: location }, replace: replace });
	};

	//#endregion
	//#region useEffects

	React.useEffect(() => {
		if (isStringNotEmpty(profile?.userId)) {
			Lead.search({ userId: profile?.userId, status: ['CREATED', 'SUBMITTED_WITH_ERROR'] }).then((list) => {
				setLeads(list);
			}).catch((error) => {
				console.log(error);
				setLeads([]);
			});

			Encounter.search({ userId: profile?.userId, status: ['SCHEDULED', 'REQUESTED', 'ASSIGNED', 'STARTED'] }).then((list) => {
				setEncounters(list);
			}).catch((error) => {
				console.log(error);
				setEncounters([]);
			});

			// encounters carry no product identity, so the subscription behind one is what
			// names the treatment on these rows
			Subscription.getByUserId(profile?.userId).then((list) => {
				setSubscriptions(list);
			}).catch((error) => {
				console.log(error);
				setSubscriptions([]);
			});
		} else {
			setLeads([]);
			setEncounters([]);
			setSubscriptions([]);
		}
	}, [profile?.userId]);

	//#endregion

	return (
		<Modal
			show={showModal}
			onHide={() => { setShowModal(false); }}
			size="lg"
			backdrop="static"
			scrollable
			centered
			fullscreen="md-down"
		>
			<Modal.Header closeButton><Modal.Title>Action Items</Modal.Title></Modal.Header>
			<Modal.Body>
				{isListNotEmpty(leads) ? leads.filter(l => ['CREATED', 'SUBMITTED_WITH_ERROR'].includes(l.status)).map((lead, index) => {
					let program = getProgramById(lead.programId, programs) ?? getProgramBySlug('wl', programs);
					if (isObjectNotEmpty(program)) {
						return (
							<Button key={`complete-evaluation-${index}`} className="shadow border-3 border-gray-500 text-ready-orange rounded-3 w-100 mb-3" variant="ready-pearl-to-ready-peach"
								onClick={() => {
									setLeadId(lead?.id);
									setSearchParams(params => {
										params.set('lid', lead?.id);

										if (!params.has('coupon') && isStringNotEmpty(lead?.affiliateInfo?.couponCode)) {
											params.set('coupon', lead?.affiliateInfo?.couponCode);
										}

										if (!params.has('v') && isStringNotEmpty(lead?.affiliateInfo?.version)) {
											params.set('v', lead?.affiliateInfo?.version);
										}

										return params;
									}, { replace: true });

									const funnelPath = buildLeadFunnelPath({ programSlug: program?.slug ?? null, lead, products });
									goTo(funnelPath ?? `${ROUTES.PROGRAM_FUNNEL}/${program?.slug}`, true);
								}}
							>
								<div className={`d-flex flex-row align-items-center justify-content-start`}>
									<div className="flex-shrink-0 text-ready-black bg-ready-orange rounded-circle p-1 d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										{['wl', 'gmd'].includes(program?.slug) &&
											<FaWeightScale size={18} className="text-ready-black" />
										}
										{['l', 'fhrt', 'sc'].includes(program?.slug) &&
											<RiInfinityLine size={18} className="text-ready-black" />
										}
										{program?.slug === "ms" &&
											<RiInfinityLine size={18} className="text-ready-black" />
										}
									</div>
									<div className="text-start ms-3 me-auto">
										<h5 className={`text-ready-black fw-extrabold m-0 p-0 fs-4`}>Complete Your {program?.name} Evaluation{!['wl', 'gmd', 'fhrt'].includes(program?.slug) ? ' for ' + getProductNameBySku(lead?.productSku, products) : ''}</h5>
										<div className={`text-ready-black fw-semibold m-0 p-0 fs-875`}>Your free evaluation for {program?.name}</div>
									</div>
									<div className="flex-shrink-0 text-ready-black d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaArrowAltCircleRight size={24} className="flex-shrink-0 text-ready-black" />
									</div>
								</div>
							</Button>
						);
					} else {
						return null;
					}
				}) : null}
				{isListNotEmpty(encounters) ? encounters.filter(e => ['SCHEDULED', 'REQUESTED'].includes(e.status) && e.visitType === 'CONSULT').map((encounter, index) => {
					let program = getProgramById(encounter.programId, programs);
					let treatmentName = getEncounterTreatmentName(encounter, { subscriptions, products });
					if (isObjectNotEmpty(program)) {
						return (
							<Button key={`complete-encounter-${index}`} className="shadow border-3 border-gray-500 text-ready-orange rounded-3 w-100 mb-3" variant="ready-pearl-to-ready-peach"
								onClick={() => { goTo(ROUTES.ENCOUNTER + `/${encounter.id}`, true); }}
							>
								<div className={`d-flex flex-row align-items-center justify-content-start`}>
									<div className="flex-shrink-0 text-ready-black bg-ready-orange rounded-circle p-1 d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaStethoscope size={18} className="text-ready-black" />
									</div>
									<div className="text-start ms-3 me-auto">
										<h5 className={`text-ready-black fw-extrabold m-0 p-0 fs-4`}>{isStringNotEmpty(treatmentName) ? `${treatmentName}: Doctor requested additional information` : 'Doctor requested additional information'}</h5>
										<div className={`text-ready-black fw-semibold m-0 p-0 fs-875`}>{encounter?.messageName || 'Provide additional information now'}</div>
									</div>
									<div className="flex-shrink-0 text-ready-black d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaArrowAltCircleRight size={24} className="flex-shrink-0 text-ready-black" />
									</div>
								</div>
							</Button>
						);
					} else {
						return null;
					}
				}) : null}
				{isListNotEmpty(encounters) ? encounters.filter(e => ['SCHEDULED'].includes(e.status) && e.visitType === 'FOLLOWUP').map((encounter, index) => {
					let program = getProgramById(encounter?.programId, programs);
					let treatmentName = getEncounterTreatmentName(encounter, { subscriptions, products });
					if (isObjectNotEmpty(program)) {
						return (
							// <Alert variant="warning" key={`draft-order-${index}`}>
							// 	<div className="d-flex justify-content-between align-items-center">
							// 		<span>Check-In for your {program?.name} {toFormattedString('EncounterVisitType', encounter.visitType)} telehealth visit?</span>
							// 		<div>
							// 			<Button onClick={() => { goTo(ROUTES.ENCOUNTER + `/${encounter.id}`, true); }} variant="warning">Check-In Now!</Button>
							// 		</div>
							// 	</div>
							// </Alert>
							<Button key={`complete-followup-encounter-${index}`} className="shadow border-3 border-gray-500 text-ready-orange rounded-3 w-100 mb-3" variant="ready-pearl-to-ready-peach"
								onClick={() => { goTo(ROUTES.ENCOUNTER + `/${encounter?.id}`, true); }}
							>
								<div className={`d-flex flex-row align-items-center justify-content-start`}>
									<div className="flex-shrink-0 d-inline-flex flex-column align-items-center justify-content-center lh-1" style={{ height: 40, width: 40 }}>
										<div className="text-ready-black fs-1250 fw-bold">{fromUtcToDeviceTimeZoneAsMoment(encounter.createdAt).format('DD')}</div>
										<div className="text-ready-orage fs-750 fw-bold text-uppercase">{fromUtcToDeviceTimeZoneAsMoment(encounter.createdAt).format('MMM')}</div>
									</div>
									<div className="text-start ms-3 me-auto">
										<h5 className={`text-ready-black fw-extrabold m-0 p-0 fs-4`}>{isStringNotEmpty(treatmentName) ? `Time for your ${treatmentName} check-in` : 'Time for your required check-in'}</h5>
										<div className={`text-ready-black fw-semibold m-0 p-0 fs-875`}>Fill out your 3-minute intake form</div>
									</div>
									<div className="flex-shrink-0 text-ready-black d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaArrowAltCircleRight size={24} className="flex-shrink-0 text-ready-black" />
									</div>
								</div>
							</Button>
						);
					} else {
						return null;
					}
				}) : null}
				{isListNotEmpty(encounters) ? encounters.filter(e => ['ASSIGNED', 'STARTED'].includes(e.status) && isBooleanFalse(e.isRead)).map((encounter, index) => {
					let program = getProgramById(encounter.programId, programs);
					// only display if there are no follow-up encounters with pending check-in
					if (isObjectNotEmpty(program) && isListEmpty(encounters.filter(e => ['SCHEDULED', 'REQUESTED'].includes(e.status) && e.visitType === 'FOLLOWUP'))) {
						return (
							<Button key={`doctor-chat-${index}`} className="shadow border-3 border-gray-500 text-ready-orange rounded-3 w-100 mb-3" variant="ready-pearl-to-ready-peach"
								onClick={() => {
									changeActiveEncounter(encounter.id);
									goTo(ROUTES.MY_MESSAGES, true);
									setShowModal(false);
								}}
							>
								<div className={`d-flex flex-row align-items-center justify-content-start`}>
									<div className="flex-shrink-0 text-ready-black bg-ready-orange rounded-circle p-1 d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaEnvelope size={18} className="text-ready-black" />
									</div>
									<div className="text-start ms-3 me-auto">
										<h5 className={`text-ready-black fw-extrabold m-0 p-0 fs-4`}>{isBooleanTrue(encounter.isRead) ? 'Message your Doctor' : 'New Message from your Doctor'}</h5>
										<div className={`text-ready-black fw-semibold m-0 p-0 fs-875`}>{isBooleanTrue(encounter.isRead) ? 'Have questions about your medication?' : 'Timely response may be needed'}</div>
									</div>
									<div className="flex-shrink-0 text-ready-black d-inline-flex align-items-center justify-content-center" style={{ height: 40, width: 40 }}>
										<FaArrowAltCircleRight size={24} className="flex-shrink-0 text-ready-black" />
									</div>
								</div>
							</Button>
						);
					} else {
						return null;
					}
				}) : null}
			</Modal.Body>
		</Modal>
	);
};

export default NotificationModal;
